import React, { useEffect, useRef, useState } from 'react';
import { If } from 'src/components/If';
import { useTranslation } from 'react-i18next';
import { useRequestCommentsContext } from 'src/features/comments/use-request-comments';
import { useRequestRevisionsStore } from 'src/features/revisions/use-revisions-context-provider';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { cn } from 'src/lib/utils';
import { Badge } from 'src/components/ui/badge';
import { Card } from 'src/components/ui/card';
import { Button } from 'src/components/ui/button/button';
import { RevisionChatInputForm } from 'src/pages/RevisionsPage/revisions-content-main/revision-chat-input-form';
import { RevisionChatComment } from 'src/pages/RevisionsPage/revisions-content-main/revision-chat-comment';
import { RevisionChatCommentReplySheet } from 'src/pages/RevisionsPage/revisions-content-main/revision-chat-comment-reply-sheet';
import ChatSystemMessage from 'src/pages/RevisionsPage/revisions-content-main/chat-system-message';
import ReEditAndApproveButtons from 'src/pages/RevisionsPage/revisions-content-main/reedit-and-approve-buttons';
import { ChatToastViewport } from 'src/components/chat-toast/chat-toast';
import { MediaUploadProvider } from 'src/pages/media-library/context/media-upload-context';
import { useToggle } from 'src/lib/state-utils';
import { useProfile } from 'src/models';
import { ArrowRight, Loader2 } from 'lucide-react';

type RevisionChatContentProps = React.ComponentProps<typeof Card>;

export const RevisionChatSection: React.FC<RevisionChatContentProps> = ({
  className,
  children,
  ...props
}) => (
  <Card
    {...props}
    className={cn('tw-isolate tw-relative tw-flex tw-min-w-0 tw-flex-col tw-gap-3 tw-overflow-hidden', className)}
    variant={'noShadow'}
  >
    {children}
  </Card>
);

const RevisionChatContent: React.FC = () => {
  const { t } = useTranslation();
  const { isOnPlatformPlan } = useProfile({ enabled: false });
  const scrollRef = useRef<HTMLDivElement>(null);

  const [currentCommentEditing, setCurrentCommentEditing] = useState<string>('');

  const request = useDecoratedRequestContext();
  const { revisions, selectedRevision, selectLatest } = useRequestRevisionsStore();

  const {
    comments,
    isLoadingMore,
    canLoadMore,
    fetchNextPage,
    hasComments,
    addCommentMutation,
    removeCommentMutation,
    reactCommentMutation,
    invalidate,
  } = useRequestCommentsContext();

  const [rateRevisionDialogOpen, setRateRevisionDialogOpen] = useToggle();

  // Banner stays mounted while any approval action is available OR while the
  // rate-revision dialog is open. The dialog gate matters because clicking
  // Re-edit optimistically sets requested_reedit_at, which immediately flips
  // canApprove / canRequestReEdit / canComplete to false on the next render —
  // without this we'd unmount the banner (and its child Dialog) before the
  // rate-revision dialog could appear.
  // Platform Plan (#2794): requests are read-only, so no Re-edit or Approve
  const showActionBanner =
    !isOnPlatformPlan &&
    (request.canApprove || request.canRequestReEdit || request.canComplete || rateRevisionDialogOpen);

  useEffect(() => {
    const timeout = setTimeout(() => scrollToBottom(), 100);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const scrollToBottom = () => {
    if (!scrollRef.current || !scrollRef.current?.scrollHeight) {
      return;
    }

    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  };

  // Flat chronological timeline (per ticket: "Messages should appear
  // chronologically within the chat feed"). Three item kinds interleave
  // by created_at: user comments, system pills, and version dividers.
  // A divider is emitted only for revisions that actually have at least
  // one comment in the current page (mirrors prior `groups` behaviour
  // and respects any `from_revision` API-level filter).
  type TimelineItem =
    | { kind: 'comment'; at: string; id: string; data: (typeof comments)[number] }
    | {
        kind: 'divider';
        at: string;
        id: string;
        data: (typeof revisions)[number];
      };

  const visibleRevisionIds = new Set(comments.map((c) => c.revision));
  const dividerItems: TimelineItem[] = revisions
    .filter((r) => visibleRevisionIds.has(r.id))
    .map((r) => ({
      kind: 'divider',
      at: r.raw.created_at,
      id: `divider:${r.id}`,
      data: r,
    }));
  const commentItems: TimelineItem[] = comments.map((c) => ({
    kind: 'comment',
    at: c.created_at,
    id: c.id,
    data: c,
  }));

  // Sort ascending: oldest at top, newest at bottom — standard chat layout
  // with `flex-col`. Tiebreaker on `id` mirrors the backend's secondary
  // order so same-second writes (e.g. moveToEditing replays editor_assigned
  // + status_changed back-to-back) render deterministically.
  const timeline = [...dividerItems, ...commentItems].sort((a, b) => {
    if (a.at !== b.at) return a.at < b.at ? -1 : 1;
    return a.id < b.id ? -1 : 1;
  });

  return (
    <>
      {showActionBanner && (
        <div className={'tw-border-b tw-border-neutral-100 tw-px-4 tw-py-3'}>
          <ReEditAndApproveButtons
            rateRevisionDialogOpen={rateRevisionDialogOpen}
            setRateRevisionDialogOpen={setRateRevisionDialogOpen}
          />
        </div>
      )}
      <ScrollArea
        className={'!tw-m-0 tw-max-h-[40vh] tw-grow tw-px-4 tw-pb-4 tw-pt-4 md:tw-max-h-[60vh]'}
        viewportProps={{
          className: '[&>div]:!tw-block',
        }}
        scrollRef={scrollRef}
        onScrollUp={(ref) => {
          if (!ref.scrollTop && canLoadMore && !isLoadingMore) {
            fetchNextPage();
          }
        }}
      >
        <If when={isLoadingMore}>
          <div className={'tw-flex tw-w-full tw-pb-4'}>
            <Loader2 className={'tw-mx-auto tw-size-8 tw-animate-spin tw-text-secondary'} />
          </div>
        </If>

        <If when={!hasComments && !isLoadingMore && !!selectedRevision}>
          <div className={'tw-text-center tw-text-text-strong'}>
            {t('This revision has no comments yet')}
          </div>
        </If>

        <If when={hasComments}>
          <div className={'tw-flex tw-flex-col tw-gap-4'}>
            {timeline.map((item) =>
              item.kind === 'divider' ? (
                <Badge
                  key={item.id}
                  variant={'brandExtraLight'}
                  className={
                    'tw-flex tw-flex-col tw-gap-1 tw-self-center tw-px-4 tw-pb-1.5 tw-pt-2'
                  }
                >
                  <div className={'tw-font-semibold tw-leading-none'}>
                    {item.data.title ?? t('Version')}
                  </div>
                  <div className={'tw-font-normal tw-leading-none'}>
                    {t('{{val, datetime}}', {
                      val: new Date(item.data.raw.created_at),
                    })}
                  </div>
                </Badge>
              ) : item.data.type === 'system' ? (
                <ChatSystemMessage key={item.id} comment={item.data} />
              ) : (
                <RevisionChatComment
                  key={item.id}
                  comment={item.data}
                  deleteMutation={removeCommentMutation}
                  reactMutation={(commentId: string, reaction: string) =>
                    reactCommentMutation.mutate([commentId, { reaction }], {
                      onSuccess: () => {
                        invalidate();
                      },
                    })
                  }
                  currentCommentEditing={currentCommentEditing}
                  setCurrentCommentEditing={setCurrentCommentEditing}
                />
              ),
            )}
          </div>
        </If>
      </ScrollArea>

      <div
        className={cn(
          'tw-relative !tw-mt-auto tw-overflow-visible tw-border-t tw-border-neutral-100 tw-p-4',
        )}
      >
        <If
          when={request.isCompleted}
          else={
            <If when={!!selectedRevision && !selectedRevision.isLatest}>
              <div
                className={
                  'tw-absolute tw-inset-0 tw-z-50 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-bg-brand-200/90 tw-p-4'
                }
              >
                <div className={'tw-text-center tw-font-medium tw-text-text-strong'}>
                  {t('Commenting is unavailable for completed revisions!')}
                </div>
                <Button size={'iconEndSmResponsive'} variant={'brand'} onClick={selectLatest}>
                  <span>{t('Go to current version')}</span>
                  <ArrowRight size={16} />
                </Button>
              </div>
            </If>
          }
        >
          <div
            className={
              'tw-absolute tw-inset-0 tw-z-50 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-bg-brand-200/90 tw-p-4'
            }
          >
            <div className={'tw-text-center tw-font-medium tw-text-text-strong'}>
              {t('Commenting is unavailable for completed revisions!')}
            </div>
          </div>
        </If>

        {/* Platform Plan (#2794): commenting is editing. Not on a completed request, which has its own overlay */}
        {/* In the flow, not an overlay: the box it replaces isn't mounted, and an older
            revision keeps its own "Go to current version" overlay */}
        <If when={isOnPlatformPlan && !request.isCompleted && (!selectedRevision || selectedRevision.isLatest)}>
          <div className={'tw-flex tw-min-h-16 tw-items-center tw-justify-center tw-rounded-md tw-bg-brand-200/90 tw-p-4'}>
            <div className={'tw-text-center tw-font-medium tw-text-text-strong'}>
              {t('alerts:platform_plan.commenting_paused', {
                defaultValue: 'Video editing is paused on Platform Plan, so comments and re-edits are off.',
              })}
            </div>
          </div>
        </If>

        {/* Viewport positioned above the input; anchored to the input wrapper */}
        <ChatToastViewport
          zIndexClassName={'tw-z-20'}
          offsetBottom={12}
          anchorId={'revision-chat-input'}
          closeOnClickAway
        />

        {/* The actual input */}
        <div id={'revision-chat-input'} className={'tw-relative tw-z-10'}>
          {/* Not mounted on Platform Plan: the overlay alone would leave it reachable by keyboard */}
          {!isOnPlatformPlan && (
            <MediaUploadProvider>
              <RevisionChatInputForm
                commentMutation={addCommentMutation}
                onSubmit={() => {
                  invalidate();
                  setTimeout(() => scrollToBottom(), 1);
                }}
              />
            </MediaUploadProvider>
          )}
        </div>
      </div>

      <RevisionChatCommentReplySheet />
    </>
  );
};

export { RevisionChatContent };
